import type { TAttentionStatusRole } from './roles'
import {
  ATTENTION_STATUS_RECIPIENT_LABEL_KEYS,
  ATTENTION_STATUS_SEE_DETAILS_ACTION_KEY,
  ATTENTION_STATUSES_NAMESPACE,
  getAttentionStatusActionKey,
} from './translations'

/**
 * Every action an opened status can offer, as the four role columns of `plans/P360-6305/Attention
 * Statuses.xlsx` name them. One id per distinct act, so the same act reads the same wherever it is
 * offered: `Remind PBM` is one action raised by seven statuses, not seven actions.
 */
export type TAttentionStatusActionId =
  | 'acknowledge'
  | 'addJustification'
  | 'callPendingAction'
  | 'createInitiative'
  | 'goToSection'
  | 'openQualityReview'
  | 'provideJustification'
  | 'remindAcknowledgment'
  | 'remindManager'
  | 'remindPbm'
  | 'reviewH2'
  | 'switchOffOffering'
  | 'updateSob'

/**
 * Where an action sits in the card's action bar. The bar is ordered by slot rather than by the
 * sheet's own numbering, so a button keeps its place across statuses: what the status asks for
 * first, what it also allows, the way to the section, and the reminder last.
 */
export type TAttentionStatusActionSlot = 'primary' | 'secondary' | 'navigation' | 'remind'

/** Who a reminder reaches. Only `slot: 'remind'` names one. */
export type TAttentionStatusRecipient = 'pbm' | 'manager' | 'partner'

export interface IAttentionStatusAction {
  id: TAttentionStatusActionId
  slot: TAttentionStatusActionSlot
  labelKey: string
  /** Who the reminder goes to, where the action is one. */
  recipient?: TAttentionStatusRecipient
  /** That recipient's own name, without the verb — what the grouped control lists. */
  recipientLabelKey?: string
}

const action = (
  id: TAttentionStatusActionId,
  slot: TAttentionStatusActionSlot,
  key: string
): IAttentionStatusAction => ({ id, slot, labelKey: `${ATTENTION_STATUSES_NAMESPACE}-${key}` })

const reminder = (
  id: TAttentionStatusActionId,
  key: string,
  recipient: TAttentionStatusRecipient
): IAttentionStatusAction => ({
  ...action(id, 'remind', key),
  recipient,
  recipientLabelKey: ATTENTION_STATUS_RECIPIENT_LABEL_KEYS[recipient],
})

/** The catalog: what each action is called and which slot it takes. */
export const ATTENTION_STATUS_ACTIONS: Record<TAttentionStatusActionId, IAttentionStatusAction> = {
  acknowledge: action('acknowledge', 'primary', 'action-acknowledge'),
  callPendingAction: action('callPendingAction', 'primary', 'action-open-actions'),
  createInitiative: action('createInitiative', 'primary', 'action-create-initiative'),
  openQualityReview: action('openQualityReview', 'primary', 'action-quality-review'),
  provideJustification: action('provideJustification', 'primary', 'action-justify'),
  reviewH2: action('reviewH2', 'primary', 'action-review'),
  updateSob: action('updateSob', 'primary', 'action-update-sob'),
  addJustification: action('addJustification', 'secondary', 'action-add-justification'),
  switchOffOffering: action('switchOffOffering', 'secondary', 'action-switch-off-offering'),
  goToSection: action('goToSection', 'navigation', 'action-open-section'),
  remindAcknowledgment: reminder('remindAcknowledgment', 'action-remind-acknowledgment', 'partner'),
  remindManager: reminder('remindManager', 'action-remind-manager', 'manager'),
  remindPbm: reminder('remindPbm', 'action-remind-pbm', 'pbm'),
}

type TRoleActions = Record<TAttentionStatusRole, TAttentionStatusActionId[]>

/* The sheet gives `Partner is not eligible for MDF` the acknowledgment status' content and its
   actions outright, so the two share one row here rather than repeating it. */
const ACKNOWLEDGMENT_ACTIONS: TRoleActions = {
  pbm: ['acknowledge', 'remindAcknowledgment'],
  manager: ['acknowledge', 'remindAcknowledgment'],
  partner: ['acknowledge'],
  other: ['remindAcknowledgment'],
}

/* Both quality statuses are reviewed the same way — one on the FinSob plan, one on the initiatives
   plan — so they carry one set of actions between them. */
const QUALITY_ACTIONS: TRoleActions = {
  pbm: ['openQualityReview', 'goToSection'],
  manager: ['remindPbm', 'goToSection'],
  partner: ['goToSection'],
  other: ['remindPbm', 'goToSection'],
}

/**
 * What each role may do on each status, transcribed from the sheet's role columns. Data rather
 * than branching: a section renders the bar it is given and never asks who is reading it.
 *
 * An empty list is the sheet's own `none` — the Partner reads those statuses and acts elsewhere.
 */
export const ATTENTION_STATUS_ROLE_ACTIONS: Record<number, TRoleActions> = {
  /** Offering X is not planned. */
  1: {
    pbm: ['goToSection', 'switchOffOffering', 'acknowledge'],
    manager: ['goToSection', 'switchOffOffering', 'remindPbm'],
    partner: ['goToSection'],
    other: ['goToSection', 'remindPbm'],
  },
  /** FinSob Plan is not good. */
  2: QUALITY_ACTIONS,
  /** Initiatives Plan is not good. */
  4: QUALITY_ACTIONS,
  /** MDF is not planned. */
  8: {
    pbm: ['createInitiative', 'goToSection', 'addJustification'],
    manager: ['goToSection', 'remindPbm'],
    partner: ['goToSection'],
    other: ['goToSection', 'remindPbm'],
  },
  /** Offering X is not fully acknowledged. */
  16: ACKNOWLEDGMENT_ACTIONS,
  /** SOB Actuals are missing. */
  32: {
    pbm: ['updateSob', 'goToSection'],
    manager: ['goToSection', 'remindPbm'],
    partner: ['goToSection'],
    other: ['goToSection', 'remindPbm'],
  },
  /** Offering X is not reviewed for H2. */
  64: {
    pbm: ['reviewH2', 'remindManager'],
    manager: ['reviewH2', 'remindPbm'],
    partner: [],
    other: ['remindPbm', 'remindManager'],
  },
  /** Partner is not eligible for MDF. */
  128: ACKNOWLEDGMENT_ACTIONS,
  /** Specific program risk. */
  256: {
    pbm: ['createInitiative', 'goToSection'],
    manager: ['remindPbm', 'goToSection'],
    partner: [],
    other: ['remindPbm', 'goToSection'],
  },
  /** QX plan is not achieved. */
  512: {
    pbm: ['provideJustification', 'goToSection'],
    manager: ['remindPbm', 'goToSection'],
    partner: ['goToSection'],
    other: ['remindPbm', 'goToSection'],
  },
  /** My pending actions. */
  1024: {
    pbm: ['callPendingAction', 'remindManager'],
    manager: ['callPendingAction', 'remindPbm'],
    partner: [],
    other: ['remindPbm', 'remindManager'],
  },
}

const SLOT_ORDER: TAttentionStatusActionSlot[] = ['primary', 'secondary', 'navigation', 'remind']

/**
 * The action bar of one status for one role, in the bar's own order. A bit the catalog grows later
 * offers nothing rather than guessing: the sheet decides what a status allows.
 */
export const getAttentionStatusActions = (
  bitValue: number,
  role: TAttentionStatusRole
): IAttentionStatusAction[] =>
  (ATTENTION_STATUS_ROLE_ACTIONS[bitValue]?.[role] ?? [])
    .map((id) => ATTENTION_STATUS_ACTIONS[id])
    .sort((left, right) => SLOT_ORDER.indexOf(left.slot) - SLOT_ORDER.indexOf(right.slot))

/**
 * The act a row's label names, per status bit. The label promises that act, so it stands only for a
 * role whose column in `ATTENTION_STATUS_ROLE_ACTIONS` offers it. A bit missing here labels its row
 * with a neutral word every role may read.
 */
const ATTENTION_STATUS_ROW_ACTION_IDS: Record<number, TAttentionStatusActionId> = {
  1024: 'callPendingAction',
  32: 'updateSob',
  64: 'reviewH2',
  16: 'acknowledge',
  256: 'createInitiative',
  512: 'provideJustification',
  4: 'openQualityReview',
  2: 'openQualityReview',
  8: 'createInitiative',
}

/**
 * The row's label for one role: the status' own act where the role may take it, `See details`
 * where the role only reads the status — `Update SOB` for the PBM, `See details` for the manager.
 */
export const getAttentionStatusRowActionKey = (
  bitValue: number,
  role: TAttentionStatusRole
): string => {
  const actionId = ATTENTION_STATUS_ROW_ACTION_IDS[bitValue]

  return actionId && !ATTENTION_STATUS_ROLE_ACTIONS[bitValue]?.[role]?.includes(actionId)
    ? ATTENTION_STATUS_SEE_DETAILS_ACTION_KEY
    : getAttentionStatusActionKey(bitValue)
}
